import React from "react";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SearchIcon from "@mui/icons-material/Search";
import HistoryIcon from "@mui/icons-material/History";

export const normalizeActivityText = (rowOrValue) => {
  if (!rowOrValue) return "";

  if (typeof rowOrValue === "string") {
    return rowOrValue.toLowerCase().trim();
  }

  return `${rowOrValue.activity_type || ""} ${rowOrValue.activity_details || ""}`
    .toLowerCase()
    .trim();
};

export const getActivityCategory = (rowOrValue) => {
  const normalized = normalizeActivityText(rowOrValue);

  if (!normalized) return "other";

  if (
    normalized.includes("search") ||
    normalized.includes("searched") ||
    normalized.includes("searching")
  ) {
    return "search";
  }

  if (
    normalized.includes("error") ||
    normalized.includes("failed") ||
    normalized.includes("exception")
  ) {
    return "error";
  }

  if (
    normalized.includes("view") ||
    normalized.includes("viewed") ||
    normalized.includes("opened") ||
    normalized.includes("screen viewed") ||
    normalized.includes("list viewed")
  ) {
    return "view";
  }

  if (
    normalized.includes("action") ||
    normalized.includes("clicked") ||
    normalized.includes("updated") ||
    normalized.includes("created") ||
    normalized.includes("saved") ||
    normalized.includes("submitted") ||
    normalized.includes("deleted") ||
    normalized.includes("closed") ||
    normalized.includes("rejected") ||
    normalized.includes("accepted")
  ) {
    return "action";
  }

  return "other";
};

export const getActivityDisplayLabel = (rowOrType, activityDetails = "") => {
  if (typeof rowOrType === "object" && rowOrType !== null) {
    return rowOrType.activity_type || rowOrType.activity_details || "Other";
  }

  return rowOrType || activityDetails || "Other";
};

export const getActivityTypeMeta = (rowOrType, activityDetails = "") => {
  const category = getActivityCategory(
    typeof rowOrType === "object"
      ? rowOrType
      : {
          activity_type: rowOrType,
          activity_details: activityDetails,
        },
  );

  const exactLabel = getActivityDisplayLabel(rowOrType, activityDetails);

  switch (category) {
    case "view":
      return {
        label: exactLabel,
        shortLabel: "View",
        color: "#1565c0",
        bg: "#e3f2fd",
        icon: <VisibilityIcon sx={{ fontSize: 16 }} />,
      };

    case "action":
      return {
        label: exactLabel,
        shortLabel: "Action",
        color: "#7b1fa2",
        bg: "#f3e5f5",
        icon: <TouchAppIcon sx={{ fontSize: 16 }} />,
      };

    case "error":
      return {
        label: exactLabel,
        shortLabel: "Error",
        color: "#c62828",
        bg: "#ffebee",
        icon: <ErrorOutlineIcon sx={{ fontSize: 16 }} />,
      };

    case "search":
      return {
        label: exactLabel,
        shortLabel: "Search",
        color: "#ef6c00",
        bg: "#fff3e0",
        icon: <SearchIcon sx={{ fontSize: 16 }} />,
      };

    default:
      return {
        label: exactLabel,
        shortLabel: "Other",
        color: "#455a64",
        bg: "#eceff1",
        icon: <HistoryIcon sx={{ fontSize: 16 }} />,
      };
  }
};

export const getActivitySummary = (logs = []) => {
  return logs.reduce(
    (acc, log) => {
      const category = getActivityCategory(log);

      acc.total += 1;

      if (category === "view") acc.views += 1;
      else if (category === "action") acc.actions += 1;
      else if (category === "error") acc.errors += 1;
      else if (category === "search") acc.searches += 1;
      else acc.others += 1;

      return acc;
    },
    {
      total: 0,
      views: 0,
      actions: 0,
      errors: 0,
      searches: 0,
      others: 0,
    },
  );
};

export const formatDateForApi = (dateValue) => {
  if (!dateValue) return "";
  const [year, month, day] = dateValue.split("-");
  return `${month}/${day}/${year}`;
};

export const formatDisplayDate = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return dateString;

  return d.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

export const formatDisplayTime = (dateString) => {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

export const buildUniqueUsers = (items = []) => {
  return Array.from(
    new Map(
      items
        .filter((item) => item?.user_id)
        .map((item) => [
          item.user_id,
          {
            user_id: item.user_id,
            user_name: item.user_name || item.user_id,
          },
        ]),
    ).values(),
  );
};

export const downloadLogsCsv = (rows = []) => {
  if (!rows.length) return;

  const headers = [
    "Log ID",
    "Activity Type",
    "Activity Category",
    "Activity Details",
    "Provider Name",
    "User ID",
    "NPI",
    "Practice ID",
    "Activity Date",
    "Activity Time",
  ];

  const csvRows = rows.map((row) => [
    row.id ?? "",
    getActivityDisplayLabel(row),
    getActivityCategory(row),
    row.activity_details ?? "",
    row.user_name ?? "",
    row.user_id ?? "",
    row.npi ?? "",
    row.practice_id ?? "",
    formatDisplayDate(row.activity_date),
    formatDisplayTime(row.activity_date),
  ]);

  const escapeCell = (value) => `"${String(value ?? "").replace(/"/g, '""')}"`;

  const csvContent = [
    headers.map(escapeCell).join(","),
    ...csvRows.map((row) => row.map(escapeCell).join(",")),
  ].join("\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);

  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", "activity_logs.csv");
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const groupLogsByDate = (logs = []) => {
  return logs.reduce((acc, item) => {
    const dateKey = formatDisplayDate(item.activity_date);
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(item);
    return acc;
  }, {});
};
